import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../components/Input';
import Dropdown from '../../../../components/Dropdown';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { ListFilters } from '../../../../interfaces/user.interface';
import useDebounce from '../../../../hooks/useDebounce';

interface FilterProps {
  roles: any[];
  allRoles: any[];
  status: any[];
  allStatus: any[];
  states: any[];
  allStates: any[];
  cities: any[];
  allCities: any[];
  areas: any[];
  allAreas: any[];
  dropdownSearchData: any;
  initialFilters: ListFilters;
  onApply: (filters: ListFilters) => void;
  onReset: () => void;
  onDropdownSearchChange: (e: any, name: string) => void;
}

const Filter: React.FC<FilterProps> = ({
  allRoles,
  roles,
  status,
  allStatus,
  states,
  allStates,
  cities,
  allCities,
  areas,
  allAreas,
  dropdownSearchData,
  initialFilters,
  onDropdownSearchChange,
  onApply,
  onReset,
}) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListFilters>(initialFilters);
  const debouncedSearchText = useDebounce(filters.searchText, 500);

  const [filtersApplied, setFiltersApplied] = useState(false);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  useEffect(() => {
    const filtersToApply = {
      ...filters,
      searchText: debouncedSearchText
    };

    const applied = Boolean(
      filtersToApply.searchText !== '' ||
      filtersToApply.role !== '' ||
      filtersToApply.status !== '1' ||
      filtersToApply.stateId !== '' ||
      (filtersToApply.cityId && filtersToApply.cityId.length > 0) ||
      (filtersToApply.areaUserIds && filtersToApply.areaUserIds.length > 0)
    );

    setFiltersApplied(applied);
    onApply(filtersToApply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearchText,
    filters.role,
    filters.status,
    filters.stateId,
    filters.cityId,
    filters.areaUserIds,
    onApply
  ]);

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      setFilters((prev: ListFilters) => {
        const newData: any = {
          ...prev,
          [name]: event.target.value,
        };
        // Cascading clear
        if (name === VALIDATION_FIELD_MAPPING.stateId) {
          newData[VALIDATION_FIELD_MAPPING.cityId as keyof ListFilters] = [];
          newData[VALIDATION_FIELD_MAPPING.areaUserIds as keyof ListFilters] = [];
        }
        if (name === VALIDATION_FIELD_MAPPING.cityId) {
          newData[VALIDATION_FIELD_MAPPING.areaUserIds as keyof ListFilters] = [];
        }
        return newData;
      });
    },
    []
  );

  const handleReset = () => {
    setFilters({
      searchText: '',
      role: '',
      status: '1',
      stateId: '',
      cityId: [],
      areaUserIds: [],
    });
    onReset();
  };

  return (
    <div className={`card mb-4 ${filtersApplied ? 'border border-primary border-2' : ''}`}>
      <div className="card-body">
        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
          <h5 className="m-0 text-dark">{t('common.filters')}</h5>
          {filtersApplied && (
            <small
              onClick={handleReset}
              className="cursor-pointer text-primary"
            >
              {t('common.clearFilters')}
            </small>
          )}
        </div>

        <div className="col-12">
          <div className="row g-3">
            <div className="col-md-3 col-sm-12">
              <Input
                type="search"
                id={VALIDATION_FIELD_MAPPING.searchText}
                name={VALIDATION_FIELD_MAPPING.searchText}
                placeholder={t('users.list.pageTexts.filters.searchPlaceholder')}
                value={filters.searchText}
                onChange={handleInputChange}
                autoComplete="on"
              />
            </div>
              <div className="col-md-3 col-sm-6">
                <Dropdown
                  values={
                    filters.role && filters.role.toString().trim() ? [String(filters.role)] : []
                  }
                  options={(roles || []).map((role: any) => ({
                    label: role.name,
                    value: String(role.id),
                  }))}
                  allOptions={(allRoles || []).map((role: any) => ({
                    label: role.name,
                    value: String(role.id),
                  }))}
                  id={VALIDATION_FIELD_MAPPING.role}
                  name={VALIDATION_FIELD_MAPPING.role}
                  searchText={dropdownSearchData.role}
                  onSearch={(e) => onDropdownSearchChange(e, 'role')}
                  placeholder={t('users.list.pageTexts.filters.selectRole')}
                  label={t('users.list.pageTexts.filters.role')}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-md-3 col-sm-6">
                <Dropdown
                  values={
                    filters.status && filters.status.toString().trim()
                      ? [String(filters.status)]
                      : []
                  }
                  options={(status || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  allOptions={(allStatus || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  id={VALIDATION_FIELD_MAPPING.status}
                  name={VALIDATION_FIELD_MAPPING.status}
                  searchText={dropdownSearchData.status}
                  onSearch={(e) => onDropdownSearchChange(e, 'status')}
                  placeholder={t('users.list.pageTexts.filters.selectStatus')}
                  label={t('users.list.pageTexts.filters.status')}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="row g-3 mt-3">
              <div className="col-md-3 col-sm-6">
                <Dropdown
                  values={
                    filters.stateId && filters.stateId.toString().trim()
                      ? [String(filters.stateId)]
                      : []
                  }
                  options={(states || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  allOptions={(allStates || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  id={VALIDATION_FIELD_MAPPING.stateId}
                  name={VALIDATION_FIELD_MAPPING.stateId}
                  searchText={dropdownSearchData.state}
                  onSearch={(e) => onDropdownSearchChange(e, 'state')}
                  placeholder={t('users.list.pageTexts.filters.selectState')}
                  label={t('users.list.pageTexts.filters.state')}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-md-3 col-sm-6">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.cityId}
                  name={VALIDATION_FIELD_MAPPING.cityId}
                  values={filters.cityId || []}
                  options={(cities || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  allOptions={(allCities || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  searchText={dropdownSearchData.city}
                  onSearch={(e) => onDropdownSearchChange(e, 'city')}
                  placeholder={t('users.list.pageTexts.filters.selectCity')}
                  label={t('users.list.pageTexts.filters.city')}
                  onChange={handleInputChange}
                  multiSelect
                />
              </div>
              <div className="col-md-3 col-sm-6">
                <Dropdown
                  values={filters.areaUserIds || []}
                  multiSelect
                  options={(areas || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  allOptions={(allAreas || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  id={VALIDATION_FIELD_MAPPING.areaUserIds}
                  name={VALIDATION_FIELD_MAPPING.areaUserIds}
                  searchText={dropdownSearchData.area}
                  onSearch={(e) => onDropdownSearchChange(e, 'area')}
                  placeholder={t('users.list.pageTexts.filters.selectArea')}
                  label={t('users.list.pageTexts.filters.area')}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default Filter;
